import { useTranslation } from 'react-i18next';

import { colorsPalettes } from '@similarweb/styles';

import { QuotaBadge } from 'components/quota-components/QuotaBadge';
import { Tooltip } from 'components/Tooltip';
import type { Crm } from 'domain/crm';
import { CrmDisplayName } from 'domain/crm';
import { useCrmStatusQuery } from 'features/crm/useCrmStatusQuery';
import { useIsDataCreditsEnabled } from 'features/quota/hooks/useIsDataCreditsEnabled';
import { useQuota } from 'features/quota/hooks/useQuota';
import { useUserQuery } from 'features/user/useUserQuery';

import {
  StyledDisabledIcon,
  StyledExportToCrmOption,
  StyledExportToCrmOptionQuota,
  StyledExportToCrmOptionTitle,
} from './styles';

type CompanyExportToCrmContainerProps = {
  crm: Crm;
  onExport: (crm: Crm) => void;
};

export const CompanyExportToCrmContainer = ({ crm, onExport }: CompanyExportToCrmContainerProps) => {
  const { t } = useTranslation();
  const { data: userData } = useUserQuery();
  const { data: crmStatus } = useCrmStatusQuery(crm);

  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();
  const { quota, canAfford, getExportCost } = useQuota();

  const isCrmIntegrated = !!crmStatus;
  const isExportEnabled = crmStatus?.isAccountExportEnabled;
  const hasAccessToManageCrm = userData?.hasAccessToManageCrm ?? false;
  const isCrmFeatureEnabled = userData?.isCrmFeatureEnabled ?? false;
  const isTooltipVisible = !hasAccessToManageCrm && isCrmFeatureEnabled;
  const isDisabled = !hasAccessToManageCrm || !isCrmFeatureEnabled;

  const renderExportOption = ({
    href,
    text,
    nonAdminText,
  }: {
    text: string;
    href: string;
    nonAdminText?: string | null;
  }) => (
    <Tooltip
      content={t('company.export_to_crm.disabled.tooltip')}
      enabled={isTooltipVisible}
      placement="top"
      minWidth={212}
    >
      <StyledExportToCrmOption isDisabled={isDisabled} data-automation="ExportToCrmOption">
        {!isDisabled ? (
          <a href={href} target="_blank" rel="noreferrer">
            {text}
          </a>
        ) : (
          <>
            {nonAdminText || text}
            <StyledDisabledIcon iconName="locked" size="sm" iconColor={colorsPalettes.carbon[100]} />
          </>
        )}
      </StyledExportToCrmOption>
    </Tooltip>
  );

  if (!isCrmIntegrated) {
    return renderExportOption({
      href: 'https://pro.similarweb.com/#/sales/management',
      text: t('company.export_to_crm.connect', { crm: CrmDisplayName[crm] }),
    });
  }

  if (!isExportEnabled) {
    return renderExportOption({
      href: `https://pro.similarweb.com/#/sales/management/${crm}`,
      text: t('company.export_to_crm.disabled', { crm: CrmDisplayName[crm] }),
      nonAdminText: t('company.export_to_crm', { crm: CrmDisplayName[crm] }),
    });
  }

  const exportCost = getExportCost(crm);
  const isExportAffordable = canAfford(exportCost);

  const handleExportToCrm = () => {
    if (quota.isDepleted || !isExportAffordable) {
      return;
    }

    onExport(crm);
  };

  return (
    <Tooltip
      content={t('company.export_to_crm.quota.tooltip')}
      placement="top"
      minWidth={244}
      enabled={!isDataCreditsEnabled ? quota.isDepleted : null}
    >
      <StyledExportToCrmOption
        isDisabled={quota.isDepleted || (isDataCreditsEnabled && !isExportAffordable)}
        onClick={handleExportToCrm}
      >
        <StyledExportToCrmOptionTitle>
          {t('company.export_to_crm', { crm: CrmDisplayName[crm] })}
        </StyledExportToCrmOptionTitle>

        {isDataCreditsEnabled ? (
          <QuotaBadge type="total" variant={isExportAffordable ? 'info' : 'warning'} value={exportCost} />
        ) : (
          <StyledExportToCrmOptionQuota isDisabled={quota.isDepleted} data-automation="ExportToCrmOptionQuota">
            {quota.remaining ? (
              <>
                {`${t('company.export_to_crm.quota')} `}
                <span>{quota.remaining}</span>
              </>
            ) : null}
          </StyledExportToCrmOptionQuota>
        )}
      </StyledExportToCrmOption>
    </Tooltip>
  );
};
